/* eslint-disable no-lonely-if */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DockyardServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { useDebounce } from "$utils/customHooks";
import { GenericSelectProps } from "./GenericSelect";
import SearchSelect from "./SearchSelect";
import useAuth from "$hooks/useAuth";

type Props = {
  value: any;
  onChange: (dockyardId, dockyard) => void;
  multiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  useRouteDockyardWarehouse?: boolean;
  valueKey?: string;
  label?: string;
  placeholder?: string;
  allowNoCompany?: boolean;
  companyId?: string;
  project?: string;
  rowsPerPage?: number;
  minCharsToTrigerSearch?: number;
  errorText?: string;
  renderOption?: (opt) => JSX.Element;
  onDataFetched?: (options: any[]) => void;
};

const getDockyardLabel = (option) =>
  `${option?.name}${option?.code ? ` - [${option?.code}]` : ""}`;

const DockyardSelect = ({
  value,
  label = "Chantier",
  onChange,
  multiple,
  required,
  disabled,
  useRouteDockyardWarehouse = false,
  valueKey = "_id",
  companyId,
  project,
  rowsPerPage = 20,
  minCharsToTrigerSearch = 2,
  errorText,
  onDataFetched,
  ...props
}: Props & Partial<GenericSelectProps>) => {
  // Input text value is also debounced to prevent too many calls
  const [inputValue, setInputValue] = useState<string>("");
  const debouncedInputValue = useDebounce(inputValue);

  // Selected option(s) stored locally
  const [localOptions, setLocalOptions] = useState<any[]>([]);

  // Track if we're updating from the parent component
  const [internalUpdate, setInternalUpdate] = useState(false);

  const previousProjectRef = useRef<string | undefined>(project);

  // Get user information to access contributorId
  const { user } = useAuth();

  // If value changes externally (from parent), update the input value to match
  useEffect(() => {
    if (!internalUpdate && value && localOptions.length > 0) {
      const selectedOption = multiple
        ? localOptions.filter((opt) =>
            (Array.isArray(value) ? value : [value]).includes(opt[valueKey])
          )
        : localOptions.find((opt) => opt[valueKey] === value);

      if (selectedOption && !multiple) {
        const newInputValue = getDockyardLabel(selectedOption);
        setInputValue(newInputValue);
      }
    }
  }, [value, localOptions, multiple, valueKey, internalUpdate]);

  // Reset the input value and local options when project changes
  useEffect(() => {
    // Check if project actually changed
    if (previousProjectRef.current !== project) {
      // Reset input and options when project changes
      setInputValue("");
      setLocalOptions([]);
      // Update the ref to the new project
      previousProjectRef.current = project;
    }
  }, [project]);

  const filters = useMemo(() => {
    const nextFilters = {
      ...(project && { projectId: project }),
      ...(companyId && { company: companyId }),
      ...(!!debouncedInputValue && { nameCode: debouncedInputValue }),
      ...(useRouteDockyardWarehouse && {
        contributorCompanyId: user?.userInfo?.userCompany?._id,
      }),
      isActive: true,
      companyDisable: false,
      projectDisable: false,
      rowsPerPage,
      page: 1,
    };

    // Check if search matches a selected option to avoid searching for the label itself
    let isSearchStringEqualsAnOption = false;

    if (multiple && Array.isArray(value)) {
      isSearchStringEqualsAnOption = (value || []).some((id) => {
        if (!Array.isArray(localOptions)) {
          // eslint-disable-next-line no-console
          console.warn("🚨 localOptions is not an array:", localOptions);
          return false;
        }
        const opt = localOptions.find((opt) => opt[valueKey] === id);
        const optLabel = opt ? getDockyardLabel(opt) : null;
        return optLabel === debouncedInputValue;
      });
    } else {
      if (!Array.isArray(localOptions)) {
        isSearchStringEqualsAnOption = false;
      } else {
        const foundOpt = localOptions.find((opt) => opt[valueKey] === value);
        const foundOptLabel = foundOpt ? getDockyardLabel(foundOpt) : null;

        isSearchStringEqualsAnOption = foundOptLabel === debouncedInputValue;
      }
    }

    // Prevents adding the search on name or code if the inputValue is the selected product label
    if (!multiple && !!debouncedInputValue && isSearchStringEqualsAnOption) {
      nextFilters.nameCode = undefined;
    }

    return nextFilters;
  }, [
    project,
    companyId,
    debouncedInputValue,
    rowsPerPage,
    multiple,
    value,
    localOptions,
    valueKey,
    useRouteDockyardWarehouse,
    user?.userInfo?.userCompany?._id,
  ]);

  // Custom onChange handler that manages the internal state
  const handleChange = useCallback(
    (selectedValue, selectedOption) => {
      setInternalUpdate(true);

      try {
        // Update local display options
        if (selectedOption) {
          if (multiple) {
            setInputValue(""); // reset search on select
            setLocalOptions(
              Array.isArray(selectedOption) ? selectedOption : [selectedOption]
            );
          } else {
            const newInputValue = getDockyardLabel(selectedOption);
            setInputValue(newInputValue);
            setLocalOptions([selectedOption]);
          }
        } else {
          setLocalOptions([]);
          setInputValue("");
        }

        // Pass to parent component
        if (onChange) {
          onChange(selectedValue, selectedOption);
        }
      } finally {
        // Reset the internal update flag after the cycle completes
        setTimeout(() => {
          setInternalUpdate(false);
        }, 0);
      }
    },
    [multiple, onChange]
  );

  const safeSetLocalOptions = useCallback((options: any) => {
    const safeOptions = Array.isArray(options)
      ? options
      : options
      ? [options]
      : [];
    setLocalOptions(safeOptions);
  }, []);

  const handleDataFetched = useCallback(
    (options: any[]) => {
      if (onDataFetched) {
        onDataFetched(options);
      }

      // Sync local options with fresh data from server if needed
      if (options && options.length > 0 && value) {
        const valueArray = Array.isArray(value) ? value : [value];
        const relevantOptions = options.filter((opt) =>
          valueArray.includes(opt[valueKey])
        );

        if (relevantOptions.length > 0) {
          setLocalOptions((prev) => {
            const hasChanges = relevantOptions.some((newOpt) => {
              const prevOpt = prev.find(
                (p) => p[valueKey] === newOpt[valueKey]
              );
              if (!prevOpt) return true;
              return (
                prevOpt.name !== newOpt.name || prevOpt.code !== newOpt.code
              );
            });

            if (hasChanges) {
              const newLocalOptions = [...prev];
              relevantOptions.forEach((newOpt) => {
                const idx = newLocalOptions.findIndex(
                  (p) => p[valueKey] === newOpt[valueKey]
                );
                if (idx >= 0) {
                  newLocalOptions[idx] = newOpt;
                } else {
                  newLocalOptions.push(newOpt);
                }
              });
              return newLocalOptions;
            }
            return prev;
          });
        }
      }
    },
    [onDataFetched, value, valueKey]
  );

  return (
    <SearchSelect
      debouncedInputValue={debouncedInputValue}
      inputValue={inputValue}
      setInputValue={setInputValue}
      localOptions={localOptions}
      setLocalOptions={safeSetLocalOptions}
      value={value}
      disabled={disabled}
      onChange={handleChange}
      multiple={multiple}
      valueKey={valueKey}
      noOptionHint="Aucun chantier ne correspond"
      queryKey={[CacheInvalidationAction.GET_DOCKYARDS, filters]}
      queryFn={
        useRouteDockyardWarehouse
          ? DockyardServices.getDockyardsWithWarehouse
          : DockyardServices.getDockyards
      }
      getOptionLabel={getDockyardLabel}
      queryfilters={filters}
      label={label}
      minCharsToTrigerSearch={minCharsToTrigerSearch}
      onDataFetched={handleDataFetched}
      {...props}
    />
  );
};

export default DockyardSelect;
